import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { searchContent } from '@plone/volto/actions/search/search';
import AuthorsLink, { personEntry } from './AuthorsLink';
import FlexCourseCard from '../../Listing/Course/FlexCourseCard';
import FlexProjectCard from '../../Listing/Project/FlexProjectCard';
import FlexPublicationCard from '../../Listing/Publication/FlexPublicationCard';
import FlexTopicCard from '../../Listing/Topic/FlexTopicCard';
import FlexPeopleCard from '../../Listing/People/FlexPeopleCard';
import FlexJobCard from '../../Listing/Job/FlexJobCard';
import FlexCompetitionCard from '../../Listing/Competition/FlexCompetitionCard';
import FlexRobotCard from '../../Listing/Robot/FlexRobotCard';
import FlexThesisCard from '../../Listing/Thesis/FlexThesisCard';
import FlexFullwidthCard from '../../Listing/Views/FlexFullwidthCard';

const EMPTY_ARRAY = [];

const CARD_BY_TYPE = {
  course: FlexCourseCard,
  project: FlexProjectCard,
  publication: FlexPublicationCard,
  topic: FlexTopicCard,
  people: FlexPeopleCard,
  job: FlexJobCard,
  competition: FlexCompetitionCard,
  robot: FlexRobotCard,
  thesis: FlexThesisCard,
};

const normalizePlainItem = (item, index) => ({
  key: item['@id'] || item.title || `item-${index}`,
  name: item.title,
  href: item['@id'] ? flattenToAppURL(item['@id']) : null,
});

/**
 * Shared renderer for related_* relation-list fields.
 *
 * mode:
 *   'links'   - one entry per line (default, matches the previous inline behaviour)
 *   'buttons' - several entries side by side, wrapping automatically
 *   'cards'   - same card/tile look as the target type's own Listing Block
 *               (fetches full catalog metadata by UID on demand)
 * kind:
 *   'person'  - People/Author items (name precedence, linkable only if People+is_linkable)
 *   'plain'   - everything else (Project/Publication/Topic/Sponsor/Course), always
 *               linked via '@id', label is item.title
 *
 * fieldKey must be unique per (content object, field) - only used for 'cards' mode,
 * to key the search subrequest.
 */
const RelatedItemsDisplay = ({ items, mode, kind, fieldKey }) => {
  const dispatch = useDispatch();
  // The Choice field's vocabulary is tokenized, so plone.restapi serializes
  // it as {token, title} instead of a plain string - see ChoiceFieldSerializer
  // in plone.restapi. Same pattern as type_of_position/type_of_talk elsewhere
  // in this codebase.
  const effectiveMode =
    (mode && typeof mode === 'object' ? mode.token : mode) || 'links';
  const sourceItems = items || EMPTY_ARRAY;

  const uids = useMemo(
    () => sourceItems.map((item) => item.UID).filter(Boolean),
    [sourceItems],
  );
  const uidsKey = uids.join(',');

  useEffect(() => {
    if (effectiveMode === 'cards' && uidsKey) {
      dispatch(
        searchContent(
          '',
          { UID: uidsKey.split(','), metadata_fields: '_all', b_size: 999 },
          fieldKey,
        ),
      );
    }
  }, [dispatch, effectiveMode, uidsKey, fieldKey]);

  const cardResults = useSelector(
    (state) => state.search.subrequests?.[fieldKey]?.items ?? EMPTY_ARRAY,
  );

  if (sourceItems.length === 0) return null;

  if (effectiveMode === 'cards') {
    const cardByUid = {};
    cardResults.forEach((brain) => {
      cardByUid[brain.UID] = brain;
    });
    const orderedCards = sourceItems
      .map((item) => cardByUid[item.UID])
      .filter(Boolean);

    if (orderedCards.length === 0) return null;

    // Mirrors PeopleListingView's container modifier so person cards (grid-horizontal,
    // narrow tiles) wrap into multiple columns like the People Listing block does,
    // instead of falling back to the single-column default meant for fullwidth cards.
    const containerClassName =
      kind === 'person'
        ? 'cards__container cards__container--people'
        : 'cards__container';

    return (
      <div className={containerClassName}>
        {orderedCards.map((item) => {
          const Card =
            CARD_BY_TYPE[item['@type']?.toLowerCase()] || FlexFullwidthCard;
          return <Card key={item['@id']} item={item} />;
        })}
      </div>
    );
  }

  const normalize = kind === 'person' ? personEntry : normalizePlainItem;

  if (effectiveMode === 'buttons') {
    const normalized = sourceItems
      .map((item, index) => normalize(item, index))
      .filter((n) => n.name);
    return (
      <div className="view-section__buttons">
        {normalized.map((entry) =>
          entry.href ? (
            <UniversalLink
              key={entry.key}
              href={entry.href}
              className="view-section__button"
            >
              {entry.name}
            </UniversalLink>
          ) : (
            <span key={entry.key} className="view-section__button">
              {entry.name}
            </span>
          ),
        )}
      </div>
    );
  }

  // mode === 'links' (default)
  if (kind === 'person') {
    return (
      <p className="view-section__value">
        <AuthorsLink authors={sourceItems} />
      </p>
    );
  }

  return (
    <div className="view-section__value">
      {sourceItems.map((item, index) => (
        <div key={item['@id'] || item.title || `item-${index}`}>
          {item['@id'] ? (
            <UniversalLink
              href={item['@id']}
              className="view-section__relation"
            >
              {item.title}
            </UniversalLink>
          ) : (
            <span className="view-section__relation">{item.title}</span>
          )}
        </div>
      ))}
    </div>
  );
};

RelatedItemsDisplay.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  mode: PropTypes.oneOfType([
    PropTypes.oneOf(['links', 'buttons', 'cards']),
    PropTypes.shape({ token: PropTypes.string, title: PropTypes.string }),
  ]),
  kind: PropTypes.oneOf(['person', 'plain']).isRequired,
  fieldKey: PropTypes.string,
};

export default RelatedItemsDisplay;
